import { z } from "zod";
import { EntityBase, Instant, Name } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";

/**
 * Event statuses (§13.3)
 * پیش‌نویس ← قطعی ← آماده‌سازی ← روز مراسم ← تسویه ← بایگانی؛ و لغوشده
 */
export const EventStatus = z.enum([
  "draft",
  "confirmed",
  "preparation",
  "event_day",
  "settlement",
  "archived",
  "cancelled",
]);
export type EventStatus = z.infer<typeof EventStatus>;

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: "پیش‌نویس",
  confirmed: "قطعی",
  preparation: "آماده‌سازی",
  event_day: "روز مراسم",
  settlement: "تسویه",
  archived: "بایگانی",
  cancelled: "لغوشده",
};

/**
 * Valid state transitions (§13.3)
 */
export const ALLOWED_EVENT_TRANSITIONS: Record<EventStatus, EventStatus[]> = {
  draft: ["confirmed", "cancelled"],
  confirmed: ["preparation", "cancelled"],
  preparation: ["event_day", "cancelled"],
  event_day: ["settlement", "cancelled"],
  settlement: ["archived"],
  archived: [],
  cancelled: [],
};

/**
 * Task statuses (§13.5)
 * انجام‌نشده / در حال انجام / انجام شد
 */
export const EventTaskStatus = z.enum(["todo", "in_progress", "done"]);
export type EventTaskStatus = z.infer<typeof EventTaskStatus>;

export const EVENT_TASK_STATUS_LABELS: Record<EventTaskStatus, string> = {
  todo: "انجام‌نشده",
  in_progress: "در حال انجام",
  done: "انجام شد",
};

/**
 * Checklist item inside a task
 */
export const TaskChecklistItem = z.object({
  id: z.string().min(1).max(50),
  text: z.string().min(1).max(250),
  done: z.boolean().default(false),
});
export type TaskChecklistItem = z.infer<typeof TaskChecklistItem>;

/**
 * Task template item in an event type checklist template (§13.5)
 * relativeOffsetMinutes: relative to event start (e.g. -2880 = -2 days, -1440 = -1 day, -240 = -4 hours, +180 = +3 hours)
 */
export const EventTypeTaskTemplate = z.object({
  id: z.string().min(1).max(50).optional(),
  title: z.string().min(1).max(250),
  relativeOffsetMinutes: z.number().int(),
  defaultRole: z.string().max(100).default(""),
  checklist: z.array(z.string().min(1).max(250)).default([]),
  note: z.string().max(1000).default(""),
});
export type EventTypeTaskTemplate = z.infer<typeof EventTypeTaskTemplate>;

/**
 * Event Type (نوع مراسم و قالب‌های چک‌لیست)
 */
export const EventTypeInput = z.object({
  name: Name,
  description: z.string().max(2000).default(""),
  active: z.boolean().default(true),
  taskTemplates: z.array(EventTypeTaskTemplate).default([]),
});
export type EventTypeInput = z.infer<typeof EventTypeInput>;

export const EventType = EntityBase.extend({
  name: Name,
  description: z.string(),
  active: z.boolean(),
  taskTemplates: z.array(EventTypeTaskTemplate),
});
export type EventType = z.infer<typeof EventType>;

/**
 * Menu line for the event (§13.2)
 */
export const EventMenuLine = z.object({
  foodId: Id,
  foodName: z.string().min(1).max(120),
  portions: z.number().int().positive(),
  pricePerPortion: Rial,
});
export type EventMenuLine = z.infer<typeof EventMenuLine>;

/**
 * Team member in the event (§13.4)
 */
export const EventTeamMemberInput = z.object({
  staffId: Id,
  staffName: z.string().min(1).max(120),
  role: z.string().min(1).max(100),
  phone: z.string().max(40).default(""),
  note: z.string().max(500).default(""),
});
export type EventTeamMemberInput = z.infer<typeof EventTeamMemberInput>;

export const EventTeamMember = EntityBase.extend({
  eventId: Id,
  staffId: Id,
  staffName: z.string(),
  role: z.string(),
  phone: z.string(),
  note: z.string(),
});
export type EventTeamMember = z.infer<typeof EventTeamMember>;

/**
 * Event task (§13.5)
 */
export const EventTaskInput = z.object({
  title: z.string().min(1).max(250),
  assignedStaffId: Id.nullable().default(null),
  assignedStaffName: z.string().max(120).nullable().default(null),
  dueAt: Instant,
  status: EventTaskStatus.default("todo"),
  checklist: z.array(TaskChecklistItem).default([]),
  note: z.string().max(2000).default(""),
});
export type EventTaskInput = z.infer<typeof EventTaskInput>;

export const EventTaskUpdateInput = z.object({
  title: z.string().min(1).max(250).optional(),
  assignedStaffId: Id.nullable().optional(),
  assignedStaffName: z.string().max(120).nullable().optional(),
  dueAt: Instant.optional(),
  status: EventTaskStatus.optional(),
  checklist: z.array(TaskChecklistItem).optional(),
  note: z.string().max(2000).optional(),
});
export type EventTaskUpdateInput = z.infer<typeof EventTaskUpdateInput>;

export const EventTask = EntityBase.extend({
  eventId: Id,
  title: z.string(),
  assignedStaffId: Id.nullable(),
  assignedStaffName: z.string().nullable(),
  dueAt: z.string(),
  status: EventTaskStatus,
  checklist: z.array(TaskChecklistItem),
  note: z.string(),
  overdueNotifiedAt: z.string().nullable(),
});
export type EventTask = z.infer<typeof EventTask>;

/**
 * Event entity (§13.2)
 */
export const EventInput = z.object({
  name: Name,
  eventTypeId: Id.nullable().default(null),
  customerId: Id,
  customerName: z.string().min(1).max(120),
  customerPhone: z.string().max(40).default(""),
  startAt: Instant,
  endAt: Instant,
  place: z.string().min(1).max(300),
  guestCount: z.number().int().positive(),
  kitchenId: Id,
  kitchenName: z.string().max(120).default(""),
  menuLines: z.array(EventMenuLine).default([]),
  needs: z.array(z.string().min(1).max(250)).default([]),
  depositAmount: Rial.default(0 as Rial),
  notes: z.string().max(4000).default(""),
});
export type EventInput = z.infer<typeof EventInput>;

export const EventUpdateInput = z.object({
  name: Name.optional(),
  customerId: Id.optional(),
  customerName: z.string().min(1).max(120).optional(),
  customerPhone: z.string().max(40).optional(),
  startAt: Instant.optional(),
  endAt: Instant.optional(),
  place: z.string().min(1).max(300).optional(),
  guestCount: z.number().int().positive().optional(),
  kitchenId: Id.optional(),
  kitchenName: z.string().max(120).optional(),
  menuLines: z.array(EventMenuLine).optional(),
  needs: z.array(z.string().min(1).max(250)).optional(),
  depositAmount: Rial.optional(),
  notes: z.string().max(4000).optional(),
});
export type EventUpdateInput = z.infer<typeof EventUpdateInput>;

export const Event = EntityBase.extend({
  name: Name,
  eventTypeId: Id.nullable(),
  customerId: Id,
  customerName: z.string(),
  customerPhone: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  place: z.string(),
  guestCount: z.number().int(),
  kitchenId: Id,
  kitchenName: z.string(),
  menuLines: z.array(EventMenuLine),
  needs: z.array(z.string()),
  depositAmount: Rial,
  status: EventStatus,
  progressPerMille: z.number().int().min(0).max(1000),
  notes: z.string(),
  orderId: Id.nullable().default(null),
  invoiceId: Id.nullable().default(null),
  costLines: z.array(z.lazy(() => EventCostLine)).default([]),
  problems: z.array(z.lazy(() => EventProblem)).default([]),
});
export type Event = z.infer<typeof Event>;

export const EventTransitionInput = z.object({
  toStatus: EventStatus,
  reason: z.string().max(500).default(""),
});
export type EventTransitionInput = z.infer<typeof EventTransitionInput>;

/**
 * Event Cost Category & Lines (§13.7)
 * rent (اجاره)، transport (حمل)، other (سایر)
 */
export const EventCostCategory = z.enum(["rent", "transport", "other"]);
export type EventCostCategory = z.infer<typeof EventCostCategory>;

export const EVENT_COST_CATEGORY_LABELS: Record<EventCostCategory, string> = {
  rent: "اجاره",
  transport: "حمل و نقل",
  other: "سایر هزینه‌ها",
};

export const EventCostLineInput = z.object({
  category: EventCostCategory,
  title: z.string().min(1).max(250),
  amountRial: Rial,
  note: z.string().max(1000).default(""),
});
export type EventCostLineInput = z.infer<typeof EventCostLineInput>;

export const EventCostLine = EntityBase.extend({
  eventId: Id,
  category: EventCostCategory,
  title: z.string(),
  amountRial: Rial,
  note: z.string(),
});
export type EventCostLine = z.infer<typeof EventCostLine>;

/**
 * Event Profit Breakdown (§13.7)
 */
export const EventFinanceProfit = z.object({
  eventId: Id,
  revenueRial: Rial,
  materialsCostRial: Rial,
  externalWagesRial: Rial,
  rentCostRial: Rial,
  transportCostRial: Rial,
  otherCostRial: Rial,
  totalCostRial: Rial,
  netProfitRial: Rial,
  marginPerMille: z.number().int(),
  depositAmount: Rial,
  paidTotalRial: Rial,
  remainingSettlementRial: Rial,
  invoiceId: Id.nullable(),
  orderId: Id.nullable(),
});
export type EventFinanceProfit = z.infer<typeof EventFinanceProfit>;

/**
 * Guest Count Change Request (§13.8, §6.5)
 */
export const EventGuestChangeRequestInput = z.object({
  newGuestCount: z.number().int().positive(),
  note: z.string().max(500).default(""),
});
export type EventGuestChangeRequestInput = z.infer<typeof EventGuestChangeRequestInput>;

export const EventGuestChangeDecisionInput = z.object({
  decision: z.enum(["accept", "reject"]),
  note: z.string().max(500).default(""),
});
export type EventGuestChangeDecisionInput = z.infer<typeof EventGuestChangeDecisionInput>;

export const EventGuestChangeRequest = EntityBase.extend({
  eventId: Id,
  oldGuestCount: z.number().int(),
  newGuestCount: z.number().int(),
  status: z.enum(["pending", "accepted", "rejected"]),
  note: z.string(),
  decisionNote: z.string(),
  decidedAt: z.string().nullable(),
  decidedByStaffId: Id.nullable(),
  decidedByStaffName: z.string().nullable(),
});
export type EventGuestChangeRequest = z.infer<typeof EventGuestChangeRequest>;

/**
 * Event Problem (§13.9)
 */
export const EventProblemInput = z.object({
  title: z.string().min(1).max(250),
  description: z.string().max(2000).default(""),
});
export type EventProblemInput = z.infer<typeof EventProblemInput>;

export const EventProblem = EntityBase.extend({
  eventId: Id,
  title: z.string(),
  description: z.string(),
  recordedByStaffId: Id,
  recordedByStaffName: z.string(),
});
export type EventProblem = z.infer<typeof EventProblem>;

/**
 * Returnable Container Return (§13.9)
 */
export const EventContainerReturnInput = z.object({
  containerTypeId: Id,
  quantity: z.number().int().positive(),
  note: z.string().max(500).default(""),
});
export type EventContainerReturnInput = z.infer<typeof EventContainerReturnInput>;

/**
 * Day sheet (§13.6)
 * A4 hour-by-hour print sheet: who, what, when, where; menu and portions; emergency contacts
 */
export const EmergencyContactItem = z.object({
  name: z.string(),
  phone: z.string(),
  role: z.string(),
});
export type EmergencyContactItem = z.infer<typeof EmergencyContactItem>;

export const DaySheet = z.object({
  event: Event,
  team: z.array(EventTeamMember),
  tasks: z.array(EventTask),
  menu: z.array(EventMenuLine),
  needs: z.array(z.string()),
  emergencyContacts: z.array(EmergencyContactItem),
  generatedAt: z.string(),
});
export type DaySheet = z.infer<typeof DaySheet>;
